import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  ArrowLeft, ArrowRight, CheckCircle, Zap, Flame, User, MapPin, CreditCard, FileText, ShieldCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { id: 1, title: "Offerta", icon: Zap },
  { id: 2, title: "Dati Fornitura", icon: MapPin },
  { id: 3, title: "Intestatario", icon: User },
  { id: 4, title: "Pagamento", icon: CreditCard },
  { id: 5, title: "Riepilogo", icon: FileText },
];

export default function SwitchRequest() {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  
  // Form State
  const [formData, setFormData] = useState({
    offer_id: location.state?.offer_id || "",
    tipo_fornitura: location.state?.type || "luce", // "luce" or "gas"
    
    // Supply
    codice_pod_pdr: "",
    indirizzo_fornitura: "",
    comune_fornitura: "",
    cap_fornitura: "",
    
    // Personal
    nome: "",
    cognome: "",
    codice_fiscale: "",
    email: "",
    telefono: "",
    
    // Payment
    metodo_pagamento: "sdd", // "sdd" or "bollettino"
    iban: "",
    intestatario_conto: "",
    
    // Consents
    consenso_privacy: false,
    consenso_marketing: false
  });

  useEffect(() => {
    fetchOffers();
    // Prefill user data if logged in
    fetchUserData();
  }, []);

  const fetchOffers = async () => {
    try {
      const res = await fetch(`${API}/offers`);
      if (res.ok) setOffers(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const fetchUserData = async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (res.ok) {
        const user = await res.json();
        setFormData(prev => ({
          ...prev,
          nome: user.name?.split(" ")[0] || "",
          cognome: user.name?.split(" ").slice(1).join(" ") || "",
          email: user.email || ""
        }));
      }
    } catch (e) {
      // Not logged in or error, ignore
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.offer_id) { toast.error("Seleziona un'offerta"); return false; }
        return true;
      case 2:
        if (!formData.codice_pod_pdr || !formData.indirizzo_fornitura || !formData.comune_fornitura || !formData.cap_fornitura) {
          toast.error("Compila tutti i campi obbligatori"); return false;
        }
        return true;
      case 3:
        if (!formData.nome || !formData.cognome || !formData.codice_fiscale || !formData.email || !formData.telefono) {
          toast.error("Compila tutti i dati anagrafici"); return false;
        }
        return true;
      case 4:
        if (formData.metodo_pagamento === "sdd" && !formData.iban) {
          toast.error("Inserisci l'IBAN per l'addebito diretto"); return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!formData.consenso_privacy) {
      toast.error("Devi accettare l'informativa sulla privacy");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/switch/requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Errore durante l'invio");
      }

      toast.success("Richiesta inviata con successo!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedOfferDetails = offers.find(o => o.offer_id === formData.offer_id);

  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground">Passa a 8energy</h1>
          <p className="text-muted-foreground mt-2">Compila il modulo per attivare la tua fornitura in pochi minuti</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-12 relative">
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-muted -z-10 -translate-y-1/2"></div>
          {steps.map((step) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            
            return (
              <div key={step.id} className="flex flex-col items-center bg-background px-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  isActive ? "border-primary bg-primary text-white" : 
                  isCompleted ? "border-primary bg-primary text-white" : 
                  "border-muted bg-background text-muted-foreground"
                }`}>
                  {isCompleted ? <CheckCircle className="w-6 h-6" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`text-xs mt-2 font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>

        {/* Form Card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="border-t-4 border-t-primary">
            <CardHeader>
              <CardTitle>{steps[currentStep-1].title}</CardTitle>
              <CardDescription>Inserisci i dati richiesti per proseguire</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* STEP 1: OFFER SELECTION */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="flex gap-4 mb-6">
                    <Button 
                      variant={formData.tipo_fornitura === "luce" ? "default" : "outline"}
                      onClick={() => handleChange("tipo_fornitura", "luce")}
                      className="flex-1 h-16 text-lg"
                    >
                      <Zap className="mr-2" /> Luce
                    </Button>
                    <Button 
                      variant={formData.tipo_fornitura === "gas" ? "default" : "outline"}
                      onClick={() => handleChange("tipo_fornitura", "gas")}
                      className="flex-1 h-16 text-lg"
                    >
                      <Flame className="mr-2" /> Gas
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {offers
                      .filter(o => o.tipo === formData.tipo_fornitura)
                      .map(offer => (
                        <div 
                          key={offer.offer_id}
                          className={`border rounded-xl p-4 cursor-pointer transition-all ${
                            formData.offer_id === offer.offer_id 
                              ? "border-primary bg-primary/5 ring-2 ring-primary ring-offset-2" 
                              : "hover:border-primary/50"
                          }`}
                          onClick={() => handleChange("offer_id", offer.offer_id)}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <h3 className="font-bold text-lg">{offer.nome}</h3>
                              <p className="text-sm text-muted-foreground">{offer.descrizione}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-primary">
                                € {formData.tipo_fornitura === "luce" ? offer.prezzo_kwh : offer.prezzo_smc}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                / {formData.tipo_fornitura === "luce" ? "kWh" : "Smc"}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* STEP 2: SUPPLY DATA */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Codice {formData.tipo_fornitura === "luce" ? "POD" : "PDR"}</Label>
                      <Input 
                        value={formData.codice_pod_pdr} 
                        onChange={(e) => handleChange("codice_pod_pdr", e.target.value.toUpperCase())}
                        placeholder={formData.tipo_fornitura === "luce" ? "IT001E..." : "1234..."}
                      />
                      <p className="text-xs text-muted-foreground">Lo trovi sulla bolletta del tuo attuale fornitore</p>
                    </div>
                    <div className="space-y-2">
                      <Label>CAP</Label>
                      <Input 
                        value={formData.cap_fornitura} 
                        onChange={(e) => handleChange("cap_fornitura", e.target.value)}
                        placeholder="00100"
                        maxLength={5}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Indirizzo di fornitura</Label>
                    <Input 
                      value={formData.indirizzo_fornitura} 
                      onChange={(e) => handleChange("indirizzo_fornitura", e.target.value)}
                      placeholder="Via Roma 1"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Comune</Label>
                    <Input 
                      value={formData.comune_fornitura} 
                      onChange={(e) => handleChange("comune_fornitura", e.target.value)}
                      placeholder="Milano"
                    />
                  </div>
                </div>
              )}

              {/* STEP 3: PERSONAL DATA */}
              {currentStep === 3 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nome</Label>
                      <Input value={formData.nome} onChange={(e) => handleChange("nome", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Cognome</Label>
                      <Input value={formData.cognome} onChange={(e) => handleChange("cognome", e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Codice Fiscale</Label>
                    <Input 
                      value={formData.codice_fiscale} 
                      onChange={(e) => handleChange("codice_fiscale", e.target.value.toUpperCase())} 
                      maxLength={16}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={formData.email} onChange={(e) => handleChange("email", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefono</Label>
                      <Input type="tel" value={formData.telefono} onChange={(e) => handleChange("telefono", e.target.value)} />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 4: PAYMENT */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label>Metodo di pagamento</Label>
                    <Select value={formData.metodo_pagamento} onValueChange={(v) => handleChange("metodo_pagamento", v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sdd">Addebito Diretto (SDD) - Consigliato</SelectItem>
                        <SelectItem value="bollettino">Bollettino Postale</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.metodo_pagamento === "sdd" && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-4">
                      <div className="space-y-2">
                        <Label>IBAN</Label>
                        <Input 
                          value={formData.iban} 
                          onChange={(e) => handleChange("iban", e.target.value.toUpperCase())}
                          placeholder="IT00 X..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Intestatario Conto</Label>
                        <Input 
                          value={formData.intestatario_conto} 
                          onChange={(e) => handleChange("intestatario_conto", e.target.value)}
                          placeholder="Nome Cognome"
                        />
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg flex items-start gap-3">
                        <ShieldCheck className="w-5 h-5 text-green-600 mt-0.5" />
                        <p className="text-sm text-green-700">
                          L'addebito diretto è il metodo più sicuro. Eviti file alle poste e non dimentichi mai una scadenza.
                          Nessun costo aggiuntivo.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}

              {/* STEP 5: SUMMARY */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <div className="bg-muted/50 p-4 rounded-lg space-y-3">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-primary" />
                      Riepilogo Dati
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Offerta:</p>
                        <p className="font-medium">{selectedOfferDetails?.nome}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Fornitura:</p>
                        <p className="font-medium">{formData.indirizzo_fornitura}, {formData.comune_fornitura}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Intestatario:</p>
                        <p className="font-medium">{formData.nome} {formData.cognome}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Pagamento:</p>
                        <p className="font-medium">{formData.metodo_pagamento === "sdd" ? "Addebito Diretto" : "Bollettino"}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-start space-x-2">
                      <Checkbox 
                        id="privacy" 
                        checked={formData.consenso_privacy}
                        onCheckedChange={(c) => handleChange("consenso_privacy", c)}
                      />
                      <Label htmlFor="privacy" className="text-sm font-normal">
                        Ho preso visione dell’informativa privacy e accetto i termini e condizioni (obbligatorio).
                      </Label>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Checkbox 
                        id="marketing" 
                        checked={formData.consenso_marketing}
                        onCheckedChange={(c) => handleChange("consenso_marketing", c)}
                      />
                      <Label htmlFor="marketing" className="text-sm font-normal">
                        Acconsento al trattamento dei miei dati per comunicazioni commerciali
                      </Label>
                    </div>
                  </div>
                </div>
              )}

            </CardContent>
          </Card>

          {/* Buttons */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1 || loading}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Indietro
            </Button>

            {currentStep < 5 ? (
              <Button onClick={nextStep} className="rounded-full bg-primary text-white">
                Continua
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading} className="rounded-full bg-primary text-white px-8">
                {loading ? "Invio in corso..." : "Conferma e Attiva"}
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
